/*
 * tbb-bid-pixel.js
 * Pushes a `bid_placed` event into the dataLayer when an AuctionPlus bid succeeds.
 *
 * AuctionPlus posts bids to the same-origin Shopify app proxy:
 *   POST /apps/auction-plus/api/auctions/{auction_id}/bids   body: {"max_amount": <n>}
 * (Captured live 2026-07 — the app exposes no JS event, so we wrap fetch + XHR.)
 *
 * Downstream: web GTM triggers on `bid_placed` -> fbq('trackCustom','BidPlaced', ..., {eventID})
 * + GA4 event -> sGTM/Stape Facebook CAPI tag (BidPlaced, deduped on event_id).
 * `content_ids` uses the VARIANT id, which matches the Meta catalog retailer_id.
 */
(function () {
  var BID_RE = /\/apps\/auction-plus\/api\/auctions\/([^\/]+)\/bids/;

  function productMeta() {
    try {
      var m = window.ShopifyAnalytics && ShopifyAnalytics.meta;
      var p = m && m.product;
      return {
        vid: (p && p.variants && p.variants[0] && p.variants[0].id) || (m && m.selectedVariantId),
        pid: p && p.id
      };
    } catch (e) { return {}; }
  }

  function bidder() {
    var b = window.__tbbBidder || {};
    var id = b.id;
    if (!id) { try { id = window.ShopifyAnalytics && ShopifyAnalytics.meta && ShopifyAnalytics.meta.page && ShopifyAnalytics.meta.page.customerId; } catch (e) {} }
    return {
      id: id ? String(id) : undefined,
      email: b.email || undefined,
      phone: b.phone || undefined,
      first_name: b.first_name || undefined,
      last_name: b.last_name || undefined,
      city: b.city || undefined,
      state: b.state || undefined,
      zip: b.zip || undefined,
      country: b.country || undefined
    };
  }

  function getCookie(name) {
    try {
      var m = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]*)'));
      return m ? decodeURIComponent(m[1]) : undefined;
    } catch (e) { return undefined; }
  }

  /* Meta user_data normalization (must match Meta's spec exactly, since we pre-hash). */
  function normEmail(s) { return String(s).trim().toLowerCase(); }
  // lowercase, strip whitespace + punctuation, keep letters/digits (incl. accented)
  function normText(s) { return String(s).toLowerCase().trim().replace(/[^a-z0-9À-ſ]/g, ''); }
  // digits only, with country code (assume US/CA when a bare 10-digit number is given)
  function normPhone(s) {
    var d = String(s).replace(/\D/g, '');
    if (d.length === 10) d = '1' + d;
    return d;
  }
  // US: first 5 digits. Otherwise lowercase, no spaces.
  function normZip(s) {
    var z = String(s).toLowerCase().replace(/\s/g, '');
    var m = z.match(/^\d{5}/);
    return m ? m[0] : z;
  }

  // SHA-256 -> lowercase hex. Async (crypto.subtle). Returns Promise<string|undefined>.
  function sha256Hex(str) {
    try {
      if (!(self.crypto && crypto.subtle && self.TextEncoder)) return Promise.resolve(undefined);
      return crypto.subtle.digest('SHA-256', new TextEncoder().encode(str)).then(function (d) {
        return Array.prototype.map.call(new Uint8Array(d), function (b) {
          return ('00' + b.toString(16)).slice(-2);
        }).join('');
      }).catch(function () { return undefined; });
    } catch (e) { return Promise.resolve(undefined); }
  }

  function fire(auctionId, amount) {
    var meta = productMeta();
    var who = bidder();
    var base = {
      event: 'bid_placed',
      event_id: (self.crypto && crypto.randomUUID) ? crypto.randomUUID() : (Date.now() + '-' + Math.random()),
      value: Number(amount) || undefined,
      currency: 'USD',
      content_ids: meta.vid ? [String(meta.vid)] : undefined,
      content_type: 'product',
      auction_id: auctionId,
      external_id: who.id,       // Shopify customer id (pseudonymous, not PII)
      fbc: getCookie('_fbc'),    // Meta click ID   - not hashed by design
      fbp: getCookie('_fbp')     // Meta browser ID - not hashed by design
    };

    // Every PII field is normalized to Meta's spec and SHA-256 hashed IN THE BROWSER,
    // so raw PII never enters the dataLayer and therefore never reaches GA4.
    // Stape's Facebook CAPI tag accepts pre-hashed values and auto-maps them to user_data.
    var pii = [
      ['email',      who.email      && normEmail(who.email)],
      ['phone',      who.phone      && normPhone(who.phone)],
      ['first_name', who.first_name && normText(who.first_name)],
      ['last_name',  who.last_name  && normText(who.last_name)],
      ['city',       who.city       && normText(who.city)],
      ['state',      who.state      && normText(who.state)],
      ['zip',        who.zip        && normZip(who.zip)],
      ['country',    who.country    && normText(who.country)]
    ].filter(function (f) { return f[1]; });

    function push() {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(base);
    }

    if (!pii.length) { push(); return; }

    Promise.all(pii.map(function (f) { return sha256Hex(f[1]); }))
      .then(function (hashes) {
        pii.forEach(function (f, i) { if (hashes[i]) base[f[0]] = hashes[i]; });
      })
      .catch(function () {})
      .then(push);
  }

  // fetch
  var origFetch = window.fetch;
  if (origFetch) {
    window.fetch = function () {
      var args = arguments;
      var url = (args[0] && args[0].url) || args[0];
      var opt = args[1] || {};
      var match = String(url || '').match(BID_RE);
      var isBid = match && String(opt.method || '').toUpperCase() === 'POST';
      var amount;
      if (isBid && opt.body) { try { amount = JSON.parse(opt.body).max_amount; } catch (e) {} }
      var p = origFetch.apply(this, args);
      if (isBid) { p.then(function (r) { if (r && r.ok) fire(match[1], amount); }).catch(function () {}); }
      return p;
    };
  }

  // XHR (belt + suspenders)
  var open = XMLHttpRequest.prototype.open, send = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url) { this.__tbbUrl = url; this.__tbbMethod = method; return open.apply(this, arguments); };
  XMLHttpRequest.prototype.send = function (body) {
    var match = String(this.__tbbUrl || '').match(BID_RE);
    if (match && String(this.__tbbMethod || '').toUpperCase() === 'POST') {
      var amount; try { amount = JSON.parse(body).max_amount; } catch (e) {}
      this.addEventListener('load', function () { if (this.status >= 200 && this.status < 300) fire(match[1], amount); });
    }
    return send.apply(this, arguments);
  };
})();
